import { FC, useCallback, useEffect, useState } from "react";
import { formatToCurrency } from "shared/utils";
import { CostItem } from "types/landlord/verificationTemplate";
import { Input, InputSize, Show } from "shared/components";
import { RoundedMathSymbol } from "../../../components/RoundedMathSymbol";
import { cx } from "utils/classnames";
import { useTemplateContext } from "../../../provider";

interface FeesComponentProps {
  feeData?: CostItem[];
  onFeeChange: (amountCents: number) => void;
  readOnly: boolean;
  isApplicantPaid: boolean;
}

const DEFAULT_MIN_FEE = {
  number: 0.51,
  cents: 51,
  string: "0.51"
};

const FeeItem: FC<{
  fee: {
    name: string;
    description: string;
    amount: { cents: number; currency: string };
  };
}> = ({ fee }) => {
  const isPartnerFeeOrProfit = ["partners_fee", "partners_profit"].includes(
    fee.name
  );

  return (
    <div
      className={cx(
        "flex items-center gap-2 text-sm",
        isPartnerFeeOrProfit ? "font-bold" : "font-medium"
      )}
    >
      <p
        className={cx(
          isPartnerFeeOrProfit ? "text-content-1000" : "text-content-300"
        )}
      >
        {fee.description}
      </p>
      <p
        className={cx(
          fee.name === "partners_profit"
            ? "text-success-700"
            : fee.name === "partners_fee"
              ? "text-danger-500"
              : "text-content-1000"
        )}
      >
        {`${fee.name === "partners_fee" ? "-" : ""}${formatToCurrency(fee.amount)}`}
      </p>
    </div>
  );
};

const convertToCents = (value: string | number): number => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return DEFAULT_MIN_FEE.cents;
  return Math.round(num * 100);
};

export const FeesComponent: FC<FeesComponentProps> = ({
  feeData,
  onFeeChange,
  readOnly,
  isApplicantPaid
}) => {
  const { setCanSave } = useTemplateContext();
  const [applicationFeeValue, setApplicationFeeValue] = useState<string | null>(
    null
  );
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [feeError, setFeeError] = useState<string | null>(null);

  const setFee = useCallback(
    (value: string | null) => {
      // Update application fee value as string (to preserve trailing zeros)
      setApplicationFeeValue(value);

      // Debounce the onFeeChange call
      if (debounceTimeout) clearTimeout(debounceTimeout);

      const newTimeout = setTimeout(() => {
        if (value !== null && !readOnly) {
          const numericValue = Number(value);

          if (numericValue < DEFAULT_MIN_FEE.number) {
            setFeeError("Application fee must be at least $0.51");
            setCanSave(false);
            return;
          } else {
            setFeeError(null);
            setCanSave(true);
          }

          const ret = convertToCents(value);
          onFeeChange(ret);
          setApplicationFeeValue(Number(ret / 100).toFixed(2));
        }
      }, 1000); // Debounce for 1 second

      setDebounceTimeout(newTimeout);
    },
    [onFeeChange, debounceTimeout, readOnly]
  );

  useEffect(() => {
    const applicationFee = feeData?.find(
      (fee) => fee.name === "application_fee"
    );
    if (
      typeof applicationFee?.amount.cents === "number" &&
      !isNaN(applicationFee.amount.cents)
    )
      setApplicationFeeValue((applicationFee.amount.cents / 100).toFixed(2));
  }, [feeData]);

  if (!feeData) return null;

  const feeIndexes = feeData.reduce((acc, fee, index) => {
    acc[fee.name] = index;
    return acc;
  }, {});

  return (
    <div className="flex items-center gap-6 h-10">
      {feeData[feeIndexes["application_fee"]] &&
      !readOnly &&
      isApplicantPaid ? (
        <>
          <div
            key={feeIndexes["application_fee"]}
            className="flex items-center gap-2 max-h-full"
          >
            <p className="text-sm font-medium text-content-300">
              {feeData[feeIndexes["application_fee"]].description}
            </p>
            <div className="flex flex-col items-center justify-center">
              <Input
                error={Boolean(feeError)}
                className="ml-auto w-full max-w-20 grow-0 text-right !mb-0"
                id="fee"
                size={InputSize.SM}
                value={applicationFeeValue ?? ""}
                placeholder="60.00"
                maxLength={5}
                onChange={(e) => setFee(e.target.value)}
              />
              {feeError ? (
                <div className="text-center pb-3 pr-3 text-sm font-normal text-danger-500">
                  {feeError}
                </div>
              ) : null}
            </div>
          </div>
          <RoundedMathSymbol symbol="-" />
        </>
      ) : null}

      <Show if={feeIndexes["application_fee"] !== undefined && readOnly}>
        <FeeItem fee={feeData[feeIndexes["application_fee"]]} />
        <RoundedMathSymbol symbol="-" />
      </Show>

      <Show if={feeIndexes["screening_fee"] !== undefined}>
        <FeeItem fee={feeData[feeIndexes["screening_fee"]]} />
      </Show>
      <Show if={feeData[feeIndexes["credit_card_processing_fee"]]}>
        <RoundedMathSymbol symbol="-" />
        <FeeItem fee={feeData[feeIndexes["credit_card_processing_fee"]]} />
      </Show>
      <Show if={feeIndexes["partners_profit"] !== undefined}>
        <RoundedMathSymbol symbol="=" />
        <FeeItem fee={feeData[feeIndexes["partners_profit"]]} />
      </Show>
      <Show if={feeIndexes["partners_fee"] !== undefined}>
        <RoundedMathSymbol symbol="=" />
        <FeeItem fee={feeData[feeIndexes["partners_fee"]]} />
      </Show>
    </div>
  );
};
